import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import SEO from '../../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <SEO 
        title="Privacy Policy | Techie Interiors" 
        description="Read our Privacy Policy to understand how Techie Interiors collects and protects your personal data." 
        url="https://techieinteriors.co.ke/privacy-policy" 
      />
      <div className="privacy-policy-header">
        <h1>Privacy Policy for Techie Interiors</h1>
      </div>

      <div className="privacy-summary">
        <p>
          Techie Interiors values your privacy. We collect only necessary data to process orders, improve your experience, and ensure secure transactions. We never sell your personal data.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>1. Introduction</h2>
        <p>
          Welcome to Techie Interiors ("Company", "we", "our", "us").
          We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, mobile application, or use our services.
        </p>
        <p>
          By accessing or using our platform, you agree to this Privacy Policy.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>2. Information We Collect</h2>
        <p>
          We collect different types of information to provide and improve our services:
        </p>

        <h3>2.1 Personal Information</h3>
        <ul>
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Billing and shipping address</li>
          <li>Payment details (processed securely via third-party providers)</li>
          <li>Account login credentials</li>
        </ul>

        <h3>2.2 Non-Personal Information</h3>
        <ul>
          <li>Browser type and version</li>
          <li>IP address</li>
          <li>Device information</li>
          <li>Pages visited and time spent</li>
          <li>Referring URLs</li>
        </ul>

        <h3>2.3 Transaction Data</h3>
        <ul>
          <li>Order history</li>
          <li>Purchase preferences</li>
          <li>Payment confirmations</li>
        </ul>

        <h3>2.4 Cookies & Tracking Technologies</h3>
        <p>We use cookies, pixels, and similar tracking technologies to:</p>
        <ul>
          <li>Improve user experience</li>
          <li>Analyze website traffic</li>
          <li>Personalize content and ads</li>
        </ul>
      </div>

      <div className="privacy-policy-section">
        <h2>3. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Process and manage orders</li>
          <li>Deliver products and services</li>
          <li>Improve website performance and user experience</li>
          <li>Communicate with you (order updates, promotions, support)</li>
          <li>Prevent fraud and enhance security</li>
          <li>Comply with legal obligations</li>
          <li>Conduct analytics and marketing campaigns</li>
        </ul>
      </div>

      <div className="privacy-policy-section">
        <h2>4. Legal Basis for Processing (GDPR Compliance)</h2>
        <p>
          If you are in applicable jurisdictions, we process your data based on:
        </p>
        <ul>
          <li>Your consent</li>
          <li>Performance of a contract</li>
          <li>Legal obligations</li>
          <li>Legitimate business interests</li>
        </ul>
      </div>

      <div className="privacy-policy-section">
        <h2>5. Sharing Your Information</h2>
        <p>We may share your information with:</p>

        <h3>5.1 Service Providers</h3>
        <ul>
          <li>Payment gateways</li>
          <li>Shipping and logistics partners</li>
          <li>Marketing and analytics providers</li>
          <li>Hosting and IT service providers</li>
        </ul>

        <h3>5.2 Legal Authorities</h3>
        <p>When required by law or to protect our rights.</p>

        <h3>5.3 Business Transfers</h3>
        <p>In case of merger, sale, or acquisition.</p>

        <p><strong>We do not sell your personal data.</strong></p>
      </div>

      <div className="privacy-policy-section">
        <h2>6. Payment Security</h2>
        <p>
          All payments are processed through secure third-party providers.
          We do not store your full credit/debit card information.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>7. Data Retention</h2>
        <p>We retain your data only as long as necessary to:</p>
        <ul>
          <li>Fulfill orders</li>
          <li>Comply with legal obligations</li>
          <li>Resolve disputes</li>
        </ul>
      </div>

      <div className="privacy-policy-section">
        <h2>8. Your Rights</h2>
        <p>
          Depending on your location, you may have the right to:
        </p>
        <div className="rights-info">
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your data</li>
            <li>Restrict or object to processing</li>
            <li>Withdraw consent</li>
            <li>Request data portability</li>
          </ul>
        </div>
        <p>
          To exercise your rights, contact us at: <strong>techieinteriors@gmail.com</strong>
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>9. Data Security</h2>
        <p>We implement industry-standard security measures including:</p>
        <div className="security-features">
          <ul>
            <li>SSL encryption</li>
            <li>Secure servers</li>
            <li>Access controls</li>
            <li>Regular security audits</li>
          </ul>
        </div>
        <p>
          However, no method of transmission over the internet is 100% secure.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>10. Third-Party Links</h2>
        <p>
          Our website may contain links to third-party websites.
          We are not responsible for their privacy practices.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>11. International Data Transfers</h2>
        <p>
          Your information may be transferred and processed outside your country.
          We ensure appropriate safeguards are in place.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>12. Children's Privacy</h2>
        <p>
          Our services are not intended for individuals under 18.
          We do not knowingly collect data from children.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>13. Marketing Communications</h2>
        <p>
          You may receive promotional emails from us.
          You can opt out at any time by:
        </p>
        <ul>
          <li>Clicking the unsubscribe link</li>
          <li>Contacting us directly</li>
        </ul>
      </div>

      <div className="privacy-policy-section">
        <h2>14. Cookies Policy</h2>
        <p>
          We use cookies to enhance your experience.
          You can control cookies via your browser settings.
        </p>
        <p>
          Please refer to our <Link to="/cookie-policy">Cookie Policy</Link> for more detailed information.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>15. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy periodically.
          Changes will be posted on this page with an updated date.
        </p>
      </div>

      <div className="privacy-policy-section">
        <h2>16. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, contact us:
        </p>
        <div className="contact-info-box">
          <p><strong>Techie Interiors</strong></p>
          <p>Email: techieinteriors@gmail.com</p>
          <p>Phone: +254748600208</p>
          <p>Address: Corner A Githunguri, Ruiru, Kenya</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;